<template>
  <v-dialog
    v-model="isOpen"
    max-width="900px"
    content-class="custom-dialog"
  >
    <v-card class="pa-5 custom-dialog-card">
      <v-card-title>
        <span class="headline">
          {{ title }}
        </span>
      </v-card-title>

      <v-card-text>
        <v-container
          grid-list-md
          pa-0
        >
          <v-layout>
            <v-flex xs12>
              <v-text-field
                v-model="updatedUser.firstname"
                :label="$t('general.forms.first_name')"
                :rules="textInputRules"
                :hint="$t('general.forms.required')"
                class="custom-input"
              />
              <v-text-field
                v-model="updatedUser.lastname"
                :label="$t('general.forms.last_name')"
                :rules="textInputRules"
                :hint="$t('general.forms.required')"
                class="custom-input"
              />
              <v-select
                v-model="updatedUser.company"
                :items="companies.map(company => ({
                  value: company.id,
                  text: company.name,
                }))"
                :label="$t('general.forms.company')"
                :no-data-text="$t('general.forms.no_data_text')"
                class="custom-input"
              />
              <v-select
                v-model="updatedUser.projects"
                multiple
                :rules="selectInputRules"
                :items="projects.map(project => ({
                  value: project.id,
                  text: project.name,
                }))"
                :label="$t('general.forms.projects')"
                :no-data-text="$t('general.forms.no_data_text')"
                class="custom-input"
              />
              <v-text-field
                v-model="updatedUser.email"
                :label="$t('general.forms.email')"
                :rules="textInputRules"
                :hint="$t('general.forms.required')"
                class="custom-input"
                :disabled="isEdit"
              />
              <v-checkbox
                v-if="$store.state.auth.user.admin"
                v-model="updatedUser.isAdmin"
                :label="$t('general.forms.admin')"
                class="custom-input"
              />
              <v-checkbox
                v-model="updatedUser.isCompanyOwner"
                :label="$t('general.forms.companyOwner')"
                class="custom-input"
              />
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-btn
          color="blue darken-1"
          flat
          class="cancel"
          @click="close"
        >
          {{ $t("general.dialogs.cancel") }}
        </v-btn>
        <v-btn
          :disabled="
            !updatedUser.firstname || !updatedUser.lastname || !updatedUser.email
          "
          color="blue darken-1"
          flat
          class="confirm"
          @click="handleSave"
        >
          {{ $t("general.dialogs.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import gql from 'graphql-tag';

export default {
  name: 'EditUserModal',
  props: {
    new: {
      type: Boolean,
      required: false,
    },
    storedUser: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      textInputRules: [value => !!value || this.$t('general.forms.required')],
      selectInputRules: [value => value.length > 0 || this.$t('general.forms.required')],
      companies: [],
      projects: [],
      updatedUser: {
        firstname: '',
        lastname: '',
        company: '',
        email: '',
        isAdmin: false,
        projects: [],
      },
      isOpen: true,
    };
  },
  computed: {
    isEdit() {
      return !this.new;
    },
    title() {
      if (this.new) return this.$t('general.admin.tabs.users.new');
      return this.$t('general.admin.tabs.users.edit');
    },
    newProjects() {
      if (this.storedUser) {
        return this.updatedUser.projects.filter(project => !this.storedUser.projects.includes(project));
      }
      return this.updatedUser.projects;
    },
    deletedProjects() {
      if (this.storedUser) {
        return this.storedUser.projects.filter(project => !this.updatedUser.projects.includes(project));
      }
      return [];
    },
  },
  watch: {
    isOpen(value) {
      if (!value) {
        this.close();
      }
    },
  },
  mounted() {
    if (this.storedUser) {
      this.updatedUser = this.storedUser;
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    handleSave() {
      if (this.new) {
        this.createUser();
      } else {
        this.updateUser();
      }
    },
    async syncCompanyOwner(userId) {
      if (this.updatedUser.isCompanyOwner && (!this.storedUser || !this.storedUser.isCompanyOwner)) {
        await this.$apollo.mutate({
          mutation: gql`
            mutation InsertOwnership ($groupId: uuid!, $userId: uuid!) {
              insert_group_owners(
                objects: {
                  groupId: $groupId
                  userId: $userId
                }
              ) {
                affected_rows
              }
            }
          `,
          variables: {
            groupId: this.updatedUser.company,
            userId,
          },
        });
      } else if (!this.updatedUser.isCompanyOwner && this.storedUser && this.storedUser.isCompanyOwner) {
        await this.$apollo.mutate({
          mutation: gql`
            mutation DeleteGroupOwners ($userId: uuid!) {
              delete_group_owners(
                where: {
                  userId: {
                    _eq: $userId
                  }
                }
              ) {
                affected_rows
              }
            }
          `,
          variables: {
            userId,
          },
        });
      }
    },
    async syncProjects(userId) {
      const deleteProjectPromises = this.deletedProjects.map(projectId => this.$apollo.mutate({
        mutation: gql`
          mutation($projectId: uuid!, $userId: uuid!) {
            delete_project_users(
              where: {
                _and: {
                  projectId: {
                    _eq: $projectId
                  },
                  userId: {
                    _eq: $userId
                  }
                }
              }
            ) {
              affected_rows
            }
          }
        `,
        variables: {
          projectId,
          userId,
        },
      }));
      await Promise.all(deleteProjectPromises);

      const newProjectPromises = this.newProjects.map(projectId => this.$apollo.mutate({
        mutation: gql`
          mutation($projectId: uuid!, $userId: uuid!) {
            insert_project_users(
              objects: { projectId: $projectId, userId: $userId }
            ) {
              returning {
                id
              }
            }
          }
        `,
        variables: {
          projectId,
          userId,
        },
      }));
      await Promise.all(newProjectPromises);
    },
    async createUser() {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation(
              $email: String!
              $firstname: String!
              $lastname: String!
              $role: String
            ) {
              createUser(
                email: $email
                firstname: $firstname
                lastname: $lastname
                role: $role
              ) {
                id
              }
            }
          `,
          variables: {
            email: this.updatedUser.email,
            firstname: this.updatedUser.firstname,
            lastname: this.updatedUser.lastname,
            role: this.updatedUser.isAdmin ? 'superuser' : 'user',
          },
        });
        await this.$apollo.mutate({
          mutation: gql`
            mutation($groupId: uuid!, $userId: uuid!) {
              insert_group_users(
                objects: { groupId: $groupId, userId: $userId }
              ) {
                returning {
                  id
                }
              }
            }
          `,
          variables: {
            groupId: this.updatedUser.company,
            userId: data.createUser.id,
          },
        });
        await this.syncProjects(data.createUser.id);
        await this.syncCompanyOwner(data.createUser.id);
        this.$emit('update');
        this.$emit('close');
      } catch (e) {
        this.$root.vtoast.show({
          message: `${this.$t('general.dialogs.error')}: ${e.message}`,
          color: 'error',
        });
      }
    },
    async updateUser() {
      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation(
              $id: String!
              $firstname: String!
              $lastname: String!
              $role: String
            ) {
              updateUser(
                id: $id
                firstname: $firstname
                lastname: $lastname
                role: $role
              ) {
                id
              }
            }
          `,
          variables: {
            id: this.updatedUser.id,
            email: this.updatedUser.email,
            firstname: this.updatedUser.firstname,
            lastname: this.updatedUser.lastname,
            role: this.updatedUser.isAdmin ? 'superuser' : 'user',
          },
        });
        const Query = this.updatedUser.associatedToGroup ?
          `update_group_users(where: {userId: {_eq: $userId}}, _set: {groupId: $groupId}) {
          affected_rows
        }` :
          `insert_group_users(objects: {groupId: $groupId, userId: $userId}) {
          returning {
              id
          }
        }`;

        await this.$apollo.mutate({
          mutation: gql`mutation($groupId: uuid!, $userId: uuid!) {
        ${Query}
      }`,
          variables: {
            groupId: this.updatedUser.company,
            userId: this.updatedUser.id,
          },
        });

        await this.syncProjects(this.updatedUser.id);
        await this.syncCompanyOwner(this.updatedUser.id);
        this.$emit('update');
        this.$emit('close');
      } catch (e) {
        this.$root.vtoast.show({
          message: `${this.$t('general.dialogs.error')}: ${e.message}`,
          color: 'error',
        });
      }
    },
  },
  apollo: {
    companies: {
      query: gql`
        query GetAllCompanies {
          group {
            name
            id
          }
        }
      `,
      update: data => data.group,
    },
    projects: {
      query: gql`
        query GetCompanyProjects($groupId: uuid!) {
          project(where: {
            groupId: {
              _eq: $groupId
            }
          }) {
            id
            name
          }
        }
      `,
      skip() {
        return !this.updatedUser.company;
      },
      variables() {
        return {
          groupId: this.updatedUser.company,
        };
      },
      update(data) {
        this.updatedUser = {
          ...this.updatedUser,
          project_users: [],
        };
        return data.project;
      },
    },
  },
};
</script>
